import {z} from 'zod';
import {
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	getSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-schema';
import {GroupField, type ISystemGroup} from './groupSchema';

export const GROUPS_SORTABLE_FIELDS = [
	GroupField.name,
	GroupField.userCount,
	GroupField.hasAccessFilters,
] as const;

export const GroupsSortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.enum(GROUPS_SORTABLE_FIELDS),
});

export type GroupsSort = z.infer<typeof GroupsSortSchema>;

export const GroupsSortableField = z.enum(GROUPS_SORTABLE_FIELDS).enum;

export const GROUPS_DEFAULT_SORT_FIELD = GroupsSortableField.name;

export const GROUPS_DEFAULT_SORT_ORDER = SortOrder.ASCENDING;

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getGroupsDefaultSort = (
	sort: Partial<GroupsSort> = {},
): GroupsSort => ({
	field: getGroupsSortField({sortField: sort.field}),
	order: getGroupsSortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getGroupsSortField = (
	params: Omit<
		GetSortFieldParams<(typeof GROUPS_SORTABLE_FIELDS)[number]>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: GROUPS_SORTABLE_FIELDS,
		defaultSortField: GROUPS_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getGroupsSortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	getSortOrder({
		...params,
		defaultSortOrder: GROUPS_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const groupEditEffectsOrder = (
	params: Omit<
		EditEffectsSortOrderParams<
			ISystemGroup,
			(typeof GROUPS_SORTABLE_FIELDS)[number]
		>,
		'sortableFields'
	>,
) =>
	editEffectsSortOrder({
		...params,
		sortableFields: GROUPS_SORTABLE_FIELDS,
	});
